import { CommonModule } from '@angular/common';
import { Component, Input, inject, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateTimeSettings } from '@cdw-ae/common-data';
import {
  CdwColDef,
  CdwSearchGridComponent,
} from '@cdw-ae/common-ui-components-search-grid';
import { DateTimeAngularService } from '@cdw-ae/date-time-angular-service';
import { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { MatchersService } from '@cdw-ae/filter-bar-matcher-configs';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { selectCustomerDateTimeSettings } from '../../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { EmptyStateOverlayComponent } from '../../../../../../components/empty-state-overlay/empty-state-overlay.component';
import { ChromeAuditEventReason } from './chrome-audit-event-reason.enum';
import { ChromeAuditEventsDataService } from './chrome-audit-events-data.service';

/**
 * Component for displaying Chrome Audit Events in a searchable, filterable grid
 *
 * Uses the getDeviceLogs API endpoint which fetches audit events from Google Admin Reports API.
 */
@Component({
  selector: 'chrome-audit-events',
  standalone: true,
  imports: [CommonModule, CdwSearchGridComponent, EmptyStateOverlayComponent, MatProgressSpinnerModule],
  templateUrl: './chrome-audit-events.component.html',
  styleUrl: './chrome-audit-events.component.scss',
})
export class ChromeAuditEventsComponent implements OnInit, OnDestroy {
  @Input() deviceId: string | null = null;

  private destroy$ = new Subject<void>();
  private readonly store = inject(Store);
  private readonly dateTimeService = inject(DateTimeAngularService);

  // Date/time settings observable
  dateTimeSettings$!: Observable<DateTimeSettings>;
  private currentSettings: DateTimeSettings | null = null;

  public columnDefs: CdwColDef[] = [];
  public filterBarSettings: FilterBarField[] = [];
  public itemLabel = 'Audit Event';
  public componentId = 'chrome-audit-events-grid';
  public readonly gray50 = TailwindColors.gray50;
  public readonly gray300 = TailwindColors.gray300;
  public readonly gray400 = TailwindColors.gray400;

  /**
   * TEMPORARY: Chrome Audit Events are hidden behind "Coming soon..." overlay
   *
   * TO ENABLE AUDIT EVENTS:
   * 1. Change showEmptyState default from `true` to `false`
   * 2. Uncomment the isEmpty$ subscription in ngOnInit()
   * 3. Update the htmlContent in the template from 'Coming soon...' to 'No audit events found'
   *
   * The data service is fully functional and connected to the getDeviceLogs API endpoint.
   */
  public showEmptyState = true; // TEMPORARY: Set to false to show real data

  // Current reason for empty state (can be set based on backend response)
  public emptyStateReason: ChromeAuditEventReason | null = null;

  // Loading state
  public isLoading = false;

  constructor(
    public chromeAuditEventsDataService: ChromeAuditEventsDataService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);

    // Subscribe to date/time settings changes to update formatting
    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.currentSettings = settings;
      });

    // TEMPORARY: Commented out to force "Coming soon..." overlay
    // TO ENABLE: Uncomment this subscription to show real audit event data
    // this.chromeAuditEventsDataService.isEmpty$
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe((isEmpty) => {
    //     this.showEmptyState = isEmpty;
    //   });

    // Set the device ID in the data service so it can fetch audit events
    if (this.deviceId) {
      this.chromeAuditEventsDataService.setDeviceId(this.deviceId);
    }

    this.initializeColumnDefs();
    this.initializeFilterSettings();
  }

  /**
   * Initialize column definitions for the grid
   * Columns: Event Type, Date, Reason, User, Description
   */
  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Event Type',
        field: 'eventType',
        sortable: true,
        filter: true,
        minWidth: 200,
      },
      {
        headerName: 'Date',
        field: 'date',
        sortable: true,
        filter: true,
        minWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) return 'N/A';
          if (!this.currentSettings)
            return new Date(params.value).toLocaleString();
          return this.dateTimeService.format(
            params.value,
            this.currentSettings,
            true
          );
        },
      },
      {
        headerName: 'Reason',
        field: 'reason',
        sortable: true,
        filter: true,
        minWidth: 200,
      },
      {
        headerName: 'User',
        field: 'user',
        sortable: true,
        filter: true,
        minWidth: 200,
      },
      {
        headerName: 'Description',
        field: 'description',
        sortable: true,
        filter: true,
        minWidth: 300,
        flex: 1,
      },
    ];
  }

  /**
   * Initialize filter bar settings using SPOG standard matchers
   * Uses MatchersService pattern consistent with other SPOG pages
   */
  private initializeFilterSettings(): void {
    this.filterBarSettings = [
      {
        name: 'eventType',
        displayName: 'Event Type',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'date',
        displayName: 'Date',
        fieldMatchers: [MatchersService.Matchers.dateTime],
      },
      {
        name: 'reason',
        displayName: 'Reason',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'user',
        displayName: 'User',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'description',
        displayName: 'Description',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
    ];
  }

  onDataLoaded(): void {
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
